import {
  makeObservable,
  computed,
  observable,
  action,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { SourceModelData } from "@/store/sources/types";
import { SyncUpdateValue } from "@/store/sync/types";
import { Uuid } from "@/domains/global/identifiers";
import { liveQuery, Subscription } from "dexie";
import { NoteObservable } from "@/store/note/NoteObservable";
import { sortBy } from "lodash-es";

export class SourceObservable extends BaseSyncModel<SourceModelData> {
  public modelKind = SyncModelKind.Source;
  private liveQuerySubscription?: Subscription;
  protected subscribedNoteId?: Uuid;

  constructor({
    id,
    data,
    store,
  }: { id: string; data: SyncUpdateValue<SourceModelData> } & WithAppStore) {
    super({ id, data, store });

    makeObservable<this, "liveQuerySubscription" | "subscribedNoteId">(this, {
      modelKind: false,
      sourceKind: computed,
      primaryLabel: computed,
      secondaryLabel: computed,
      associatedArtifactId: computed,
      associatedAudioRecordingId: computed,
      associatedNote: computed,
      liveQuerySubscription: false,
      subscribedNoteId: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,
    });

    onBecomeObserved(this, "associatedNote", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "associatedNote", () => this.unsubscribeLiveQuery());
  }

  get sourceKind() {
    return this.modelData.source_kind;
  }

  get primaryLabel() {
    return this.modelData.primary_label;
  }

  get secondaryLabel() {
    return this.modelData.secondary_label;
  }

  get associatedArtifactId() {
    return this.modelData.associated_artifact_id;
  }

  get associatedAudioRecordingId() {
    return this.modelData.associated_audio_recording_id ?? null;
  }

  subscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(async () => {
      const matchingNoteSources = await this.store.noteSources.localTable
        .where("source_id")
        .equals(this.id)
        .toArray();
      const ids =
        matchingNoteSources
          ?.map(noteSource => noteSource.model_data?.note_id)
          .filter((id): id is string => Boolean(id)) ?? [];
      const sortedIds = sortBy(ids);
      const notes = await this.store.notes.localTable.bulkGet(sortedIds);
      const note = notes.find(note => note?.is_available);
      return note?.model_id;
    }).subscribe({
      next: noteId => {
        runInAction(() => {
          this.subscribedNoteId = noteId;
        });
      },
    });
  }

  unsubscribeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
  }

  get associatedNote(): NoteObservable | undefined {
    if (!this.subscribedNoteId) return undefined;
    const note = this.store.notes.get(this.subscribedNoteId);
    if (!note || !note.isAvailable) return undefined;
    return note;
  }
}
